import is from '@sindresorhus/is';

import type {
    Diff,
    DiffValue,
    DiffValueSkillChanges,
    EntityIdentifier,
    SkillChangeRow,
} from '../../../data-contract/chat';

const COMPARE_MIN_LENGTH = 100;

export const textValue = (value: DiffValue): string => (value.type === 'text' ? value.value : '');

export const diffSupportsCompare = (diff: Diff): boolean => {
    if (diff.isMain === true) return false;
    const longest = Math.max(textValue(diff.oldValue).length, textValue(diff.newValue).length);
    return longest > COMPARE_MIN_LENGTH;
};

// TODO: legacy entities skill diff, superseded by skill-changes — remove later.
export const isEntityDiff = (diff: Diff): boolean =>
    diff.oldValue.type === 'entities' || diff.newValue.type === 'entities';

const entityRefs = (value: DiffValue): readonly EntityIdentifier[] => (value.type === 'entities' ? value.refs : []);

const sameEntity = (a: EntityIdentifier, b: EntityIdentifier): boolean =>
    a.type === b.type && is.nonEmptyString(a.id) && a.id === b.id;

export type EntityDiffRow = {
    readonly change: 'add' | 'remove';
    readonly entity: EntityIdentifier;
    readonly key: string;
};

export const entityDiffRows = (diff: Diff): readonly EntityDiffRow[] => {
    const oldRefs = entityRefs(diff.oldValue);
    const newRefs = entityRefs(diff.newValue);
    const removed = oldRefs.filter((entity) => !newRefs.some((ref) => sameEntity(ref, entity)));
    const added = newRefs.filter((entity) => !oldRefs.some((ref) => sameEntity(ref, entity)));
    return [
        ...removed.map((entity, index) => ({ change: 'remove' as const, entity, key: `remove-${entity.id ?? index}` })),
        ...added.map((entity, index) => ({ change: 'add' as const, entity, key: `add-${entity.id ?? index}` })),
    ];
};

const skillChangesValue = (value: DiffValue): DiffValueSkillChanges | null =>
    value.type === 'skill-changes' ? value : null;

export const isSkillChangesDiff = (diff: Diff): boolean =>
    diff.oldValue.type === 'skill-changes' || diff.newValue.type === 'skill-changes';

export const skillChangeRows = (diff: Diff): readonly SkillChangeRow[] =>
    skillChangesValue(diff.newValue)?.rows ?? skillChangesValue(diff.oldValue)?.rows ?? [];

export const isEmptyDiff = (diff: Diff): boolean => {
    if (isSkillChangesDiff(diff)) return skillChangeRows(diff).length === 0;
    return isEntityDiff(diff) && entityDiffRows(diff).length === 0;
};
